import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./MangaDetails.css";

// Shared components
import LoadingSpinner from "../components/shared/LoadingSpinner";
import ErrorMessage from "../components/shared/ErrorMessage";
import MediaTabs from "../components/shared/MediaTabs";

// Media detail components
import MediaPoster from "../components/mediaDetails/MediaPoster";
import MediaHeader from "../components/mediaDetails/MediaHeader";
import StatsGrid from "../components/mediaDetails/StatsGrid";

// Manga-specific components
import MangaAbout from "../components/mangaDetails/MangaAbout";
import MangaPublication from "../components/mangaDetails/MangaPublication";
import MangaCreators from "../components/mangaDetails/MangaCreators";

// Get API URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

// Helper for conditional logging
const logError = (message, ...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(message, ...args);
  }
};

const logDebug = (message, ...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(message, ...args);
  }
};

const MangaDetails = () => {
  const { id } = useParams();
  const [manga, setManga] = useState(null);
  const [activeTab, setActiveTab] = useState("about");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    
    fetch(`${API_BASE_URL}/api/manga/${id}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch manga: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setManga(data);
        setIsLoading(false);
      })
      .catch((err) => {
        logError("Error:", err);
        setError(err.message);
        setIsLoading(false);
      });
  }, [id]);

  const handleStatusChange = (newStatus) => {
    logDebug(`Manga status changed to: ${newStatus}`);
    // You can update UI or state based on status changes if needed
  };

  // Formatting helpers
  const formatDate = (dateString) => {
    if (!dateString) return "Present";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatNumber = (num) => num?.toLocaleString() ?? 'N/A';

  // Tab configuration
  const tabs = [
    { id: 'about', label: 'About' },
    { id: 'publication', label: 'Publication' },
    { id: 'creators', label: 'Creators' }
  ];

  // Loading state
  if (isLoading) return <LoadingSpinner />;
  
  // Error state
  if (error) return <ErrorMessage message={error} />;

  // Stats configuration for the StatsGrid component
  const statsConfig = [
    { label: "Score", value: manga.score || '-' },
    { label: "Rank", value: `#${formatNumber(manga.rank)}` },
    { label: "Popularity", value: `#${formatNumber(manga.popularity)}` },
    { label: "Members", value: formatNumber(manga.members) },
    { label: "Chapters", value: manga.chapters || '-' },
    { label: "Volumes", value: manga.volumes || '-' }
  ];

  return (
    <div className="manga-details">
      {/* Hero Section */}
      <div className="manga-hero" style={{ backgroundImage: `url(${manga.images?.webp?.large_image_url})` }}>
        <div className="manga-hero-content">
          <MediaPoster 
            imageUrl={manga.images?.webp?.large_image_url}
            itemId={manga.id}
            itemType="manga"
            totalChapters={manga.chapters}
            totalVolumes={manga.volumes}
            onStatusChange={handleStatusChange}
            alt={manga.title}
          />
          
          <div className="manga-info">
            <MediaHeader 
              title={manga.title}
              englishTitle={manga.title_english}
              japaneseTitle={manga.title_japanese}
              synonyms={manga.title_synonyms}
            />

            <StatsGrid stats={statsConfig} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="manga-content-wrapper">
        <MediaTabs 
          tabs={tabs} 
          activeTab={activeTab} 
          onTabChange={setActiveTab} 
        />

        {activeTab === 'about' && <MangaAbout manga={manga} />}
        {activeTab === 'publication' && <MangaPublication manga={manga} formatDate={formatDate} />}
        {activeTab === 'creators' && <MangaCreators manga={manga} />}
      </div>

      {/* Additional Information Footer */}
      <div className="manga-additional-info">
        <span>Added: {new Date(manga.createdAt).toLocaleDateString()}</span>
        <span>Last updated: {new Date(manga.updatedAt).toLocaleDateString()}</span>
      </div>
    </div>
  );
};

export default MangaDetails;